/**
 * Inbound + DNC hooks. Inbound polls modestly (15s) — replies don't arrive
 * as constantly as outbound DLRs. DNC is more static so 30s.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@/lib/api';

export interface InboundItem {
  id: number;
  from_phone: string;
  to_phone: string;
  body: string;
  is_keyword: boolean;
  keyword?: string;
  provider_msg_id?: string;
  received_at: string;
}

export interface InboundListResponse {
  items: InboundItem[];
  total: number;
}

export interface DNCItem {
  id: number;
  phone_e164: string;
  reason?: string;
  source?: string;
  added_at: string;
}

export interface DNCListResponse {
  items: DNCItem[];
  total: number;
}

const INBOUND_KEY = ['inbound'] as const;
const DNC_KEY = ['dnc'] as const;

export function useInboundList({ enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: INBOUND_KEY,
    queryFn: () => api.get<InboundListResponse>('/v1/inbound?limit=100'),
    enabled,
    refetchInterval: enabled ? 15_000 : false,
    refetchIntervalInBackground: false,
    staleTime: 5_000,
  });
}

export function useDNCList({ enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: DNC_KEY,
    queryFn: () => api.get<DNCListResponse>('/v1/dnc?limit=200'),
    enabled,
    refetchInterval: enabled ? 30_000 : false,
    refetchIntervalInBackground: false,
    staleTime: 10_000,
  });
}

export function useAddDNC() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { phone_e164: string; reason?: string }) =>
      api.post<{ phone_e164: string }>('/v1/dnc', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: DNC_KEY });
    },
  });
}

export function useRemoveDNC() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (phone: string) => api.delete<void>(`/v1/dnc/${encodeURIComponent(phone)}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: DNC_KEY });
    },
  });
}

export interface DNCImportResult {
  total_rows: number;
  inserted: number;
  skipped: number;
  invalid: number;
}

/**
 * Bulk DNC upload (CSV/XLSX). Goes through the shared api client via
 * `options.body` so the browser owns the multipart boundary and the
 * request inherits the 401 -> refresh -> retry handling — same reasoning
 * as the contact-list import.
 */
export function useImportDNC() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => {
      const fd = new FormData();
      fd.append('file', file);
      return api.post<DNCImportResult>('/v1/dnc/import', undefined, { body: fd });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: DNC_KEY });
    },
  });
}
